import AccordionMUI from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { getUniqueID } from "../../utils/numbers";
import {
  getReactHookFormNestedError,
  getFirstNestedFieldErrorMessage,
} from "../../utils/errors";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import TooltipOptimization from "../Tooltip/TooltipOptimization";

const Accordion = ({
  id = getUniqueID(),
  children,
  title = "",
  titleAlign = "center",
  sx,
  sxTitle = {},
  sxContainerTitle = {},
  hasBorder = true,
  actionsComponent,
  variantTitle = "body1",
  initialValue = false,
  iconTitle = null,
  minHeight = "48px !important",
  onCallbackChangeExpanded = () => {},
  name = "",
  errors = {},
  showError = false,
  showErrorMessage = showError,
  showTooltip,
  titleTooltip = "",
  actionTooltip = "",
}) => {
  const [isExpanded, setIsExpanded] = useState(initialValue);
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width:450px)");

  const error = getReactHookFormNestedError(errors, name);
  const hasError =
    showError &&
    Boolean(error?.message || getFirstNestedFieldErrorMessage(error));

  useEffect(() => {
    onCallbackChangeExpanded(isExpanded);
    // eslint-disable-next-line
  }, [isExpanded]);

  return (
    <>
      <TooltipOptimization
        isOpen={showTooltip}
        titleTooltip={titleTooltip}
        actionTooltip={actionTooltip}
      >
        <AccordionMUI
          id={id}
          data-field={name || id}
          expanded={isExpanded}
          onChange={(_, value) => setIsExpanded(value)}
          defaultExpanded={initialValue}
          TransitionProps={{
            timeout: 350,
          }}
          sx={{
            borderRadius: "10px !important",
            ...sx,
            border: hasBorder
              ? `${
                  hasError ? theme.palette.error.main : INPUT_COLOR_BORDER
                } 1px solid`
              : "none",
            "& .MuiAccordionDetails-root": {
              padding: "0px 16px 16px",
              ...(sx?.["& .MuiAccordionDetails-root"] || {}),
            },
            "::before": {
              backgroundColor: "transparent",
            },
            ".MuiAccordionSummary-root": {
              ...(sx?.[".MuiAccordionSummary-root"] || {}),
              "& .MuiAccordionSummary-content": {
                m: 0,
              },
              minHeight,
            },
          }}
          elevation={0}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`${title}-content`}
            id={`${title}-header`}
            sx={{
              ".MuiAccordionSummary-content": {
                display: "flex",
                alignItems: "left",
                flexDirection: isMobile ? "column" : "row",
                gap: isMobile ? 0 : 1,
              },
              ...(actionsComponent && {
                "& .MuiAccordionSummary-expandIconWrapper": {
                  ml: 1.5,
                },
              }),
              ...sxContainerTitle,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flex: 1,
              }}
            >
              {iconTitle && iconTitle}
              <Typography
                sx={{
                  ...(variantTitle === "body1" && { fontSize: "1.05rem" }),
                  ...sxTitle,
                }}
                align={titleAlign}
                variant={variantTitle}
              >
                {title}
              </Typography>
            </Box>
            {actionsComponent && actionsComponent}
          </AccordionSummary>
          <AccordionDetails>{children}</AccordionDetails>
        </AccordionMUI>
      </TooltipOptimization>

      {hasError && showErrorMessage && error?.message ? (
        <Typography variant="caption" color="error">
          {error.message}
        </Typography>
      ) : null}
    </>
  );
};

export default Accordion;
